<template>
  <BaseModal ref="modalRef" class="max-w-[500px]!" @close="resetForm">
    <form v-if="step === 'VERIFY'" action="" class="bounce-in" @submit.prevent="completeStep()">
      <div class="">
        <div class="mb-3 flex flex-col gap-y-2 items-center">
          <h2 class="text-2xl text-center font-semibold text-white">Reset password</h2>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <div class="input-group">
            <div class="input-label">Username</div>
            <input v-model="username" type="text" class="input-item w-full" />

            <template v-if="$v.username.$dirty">
              <span v-for="error in $v.username.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
          <div class="input-group">
            <div class="input-label">What is your date of birth?</div>
            <Datepicker
              v-model="dateOfBirth"
              model-type="yyyy-MM-dd"
              :format="'yyyy-MM-dd'"
              :enable-time-picker="false"
              auto-apply
              :input-class="'w-full py-4 border-b border-blue-light placeholder:text-blue-light text-blue-te focus:outline-none focus:ring-blue-500 focus:border-primary'"
              name="dob"
              autocomplete="off"
              :teleport-center="useHelpers().isMobileDevice()"
            />
            <template v-if="$v.dateOfBirth.$dirty">
              <span v-for="error in $v.dateOfBirth.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
          <p v-if="errorMessage" class="text-cw-red">{{ errorMessage }}</p>
        </div>
        <div class="w-max ml-auto mt-4">
          <BaseButton background-color="var(--color-cw-lilac)" border-color="#1B0623" class="px-10 py-3.5 mt-8" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Continue</p>
          </BaseButton>
        </div>
      </div>
    </form>
    <form v-else-if="step === 'SET_PASSWORD'" action="" class="bounce-in" @submit.prevent="completeStep()">
      <div class="">
        <div class="mb-8 flex flex-col gap-y-2 items-center">
          <h2 class="text-2xl text-center font-semibold text-white">Set your new password</h2>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <BasePasswordInput v-model="newPassword" label="Enter new password">
            <template #errors>
              <template v-if="$v.newPassword.$dirty">
                <span v-for="error in $v.newPassword.$errors" :key="error.$uid" class="input-error">
                  {{ error.$message }}
                </span>
              </template>
            </template>
          </BasePasswordInput>
          <BasePasswordInput v-model="confirmPassword" label="Re-enter new password">
            <template #errors>
              <template v-if="$v.confirmPassword.$dirty">
                <span v-for="error in $v.confirmPassword.$errors" :key="error.$uid" class="input-error">
                  {{ error.$message }}
                </span>
              </template>
            </template>
          </BasePasswordInput>
          <p v-if="errorMessage" class="text-cw-red">{{ errorMessage }}</p>
        </div>
        <div class="w-max ml-auto mt-4">
          <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="px-10 py-3.5 ml-auto mt-8" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Set password</p>
          </BaseButton>
        </div>
      </div>
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import { useTemplateRef, ref } from 'vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BasePasswordInput from '@/components/base/BasePasswordInput.vue'
import Loader from '@/components/interface/Loader.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import AuthService from '@/services/api/auth-service'
import { useHelpers } from '@/composables/useHelpers'

const modalRef = useTemplateRef('modalRef')
const step = ref<'VERIFY' | 'SET_PASSWORD'>('VERIFY')
const username = ref('')
const dateOfBirth = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const errorMessage = ref('')
const isLoading = ref(false)
const { sleep, showToast } = useHelpers()

const $v = useVuelidate(
  {
    username: {
      required: helpers.withMessage('Enter your username', required),
      $autoDirty: true
    },
    dateOfBirth: {
      required: helpers.withMessage('Enter your date of birth', required),
      $autoDirty: true
    },
    newPassword: {
      required: helpers.withMessage('Enter your password', required),
      $autoDirty: true
    },
    confirmPassword: {
      required: helpers.withMessage('Confirm your password', required),
      $autoDirty: true
    }
  },
  { username, dateOfBirth, newPassword, confirmPassword }
)

const open = () => {
  modalRef.value?.open()
}

const close = () => {
  modalRef.value?.close()
}

const resetForm = async () => {
  await sleep(200)
  step.value = 'VERIFY'
  username.value = ''
  dateOfBirth.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
  errorMessage.value = ''
  $v.value.$reset()
}

const resetPassword = async () => {
  try {
    isLoading.value = true
    await AuthService.passwordReset({
      username: username.value,
      dateOfBirth: dateOfBirth.value,
      password: newPassword.value
    })
    close()
    showToast('Password reset successful. You can now log in with your new password.')
  } catch (error: any) {
    errorMessage.value = error?.data?.message || 'An error occurred. Please try again.'
    step.value = 'VERIFY'
  } finally {
    isLoading.value = false
  }
}

const completeStep = async () => {
  let isValid = false
  if (step.value === 'VERIFY') {
    isValid = (await $v.value.dateOfBirth.$validate()) && (await $v.value.username.$validate())
    if (!isValid) return
    step.value = 'SET_PASSWORD'
  } else {
    isValid = (await $v.value.newPassword.$validate()) && (await $v.value.confirmPassword.$validate())
    if (!isValid) return
    resetPassword()
  }
}

defineExpose({
  open,
  close
})
</script>
